import { useCustom, useTranslate } from '@refinedev/core';
import { Alert, Spin, Typography } from 'antd';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  PATHWAY_DETAIL_FIELDS,
  draftFromDetail,
  emptyPathwayDraft,
  type PathwayLibrary,
} from './model';
import { PathwayBuilder } from './PathwayBuilder';
import type { PathwayDetail } from '../../schema/be-graphql-generated';

/**
 * E34 · the pathway Builder's page — `/pathways/new` (a new pathway: DefinePathway),
 * `/pathways/new?from=<pathwayId>` (Duplicate: the Builder opens on a COPY — " (copy)", copiedFrom the
 * original; the save is the practice's own version 1), `/pathways/<pathwayId>/edit` (the practice's own:
 * Save pathway → "Save as version N?" → RevisePathway). The pathway is read once (`pathway` — the latest
 * version, whole) with the practice's capabilities (`pathways` — publish courses offers the public
 * course); the draft lives in the Builder until Save.
 */
export const PathwayBuilderPage = () => {
  const t = useTranslate();
  const navigate = useNavigate();
  const { pathwayId } = useParams();
  const [search] = useSearchParams();
  const from = search.get('from');
  const loadId = pathwayId ?? from;

  const { data, isLoading, isError } = useCustom<PathwayDetail>({
    url: '',
    method: 'get',
    errorNotification: false,
    queryOptions: { enabled: Boolean(loadId) },
    meta: {
      operation: 'pathway',
      fields: PATHWAY_DETAIL_FIELDS,
      variables: { pathwayId: { name: 'pathwayId', type: 'ID!', value: loadId } },
    },
  });
  const { data: libraryData, isLoading: capsLoading } = useCustom<PathwayLibrary>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'pathways',
      fields: ['capabilities', { pathways: ['pathwayId'] }],
      variables: { includeRetired: { name: 'includeRetired', type: 'Boolean', value: false } },
    },
  });

  if ((loadId && isLoading) || capsLoading) return <Spin />;
  const detail = data?.data ?? null;
  if (loadId && (isError || !detail)) {
    return <Alert type="error" showIcon message={t('pathways.builder.notFound')} />;
  }
  const mode = pathwayId ? 'revise' : 'define';
  const initial = detail
    ? draftFromDetail(detail, pathwayId ? 'edit' : 'copy', t('pathways.builder.copySuffix'))
    : emptyPathwayDraft();
  const leave = () => navigate('/pathways');

  return (
    <PathwayBuilder
      key={loadId ?? 'new'}
      mode={mode}
      initial={initial}
      pathwayId={pathwayId ?? null}
      version={pathwayId ? detail?.version ?? null : null}
      runningEnrollments={pathwayId ? detail?.runningEnrollments ?? 0 : 0}
      mayPublish={(libraryData?.data?.capabilities ?? []).includes('publishCourses')}
      onSaved={leave}
      onCancel={leave}
      heading={
        <Typography.Title level={3} style={{ margin: 0 }}>
          {t(
            pathwayId
              ? 'pathways.builder.titleEdit'
              : from
              ? 'pathways.builder.titleCopy'
              : 'pathways.builder.titleNew',
          )}
        </Typography.Title>
      }
    />
  );
};
